import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../Button";

import DownArrow from "../../../assets/icons/DownArrow";
import { ThemeContext } from "../../App";
import { useEvent } from "../../../Store/event";
import { useUser } from "../../../Store/user";
import { ENVIRONMENT, STAGING_ENVIRONMENT_KEY } from "../../../constants";
import { notifyPhotographer } from "../../../apis/favourites";
const NOTIFICATION_COOLDOWN = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
const NOTIFICATION_STORAGE_KEY = 'favorite_list_notification_history';

const GoBackSection = ({
  changeGallery,
  onFolderClickHandler,
  title = "Go Back",
  folderDetails = "",
  backLink = "/",
  addAnchorTag,
  containerClasses = "mb-[30px] mt-4",
  showFavouriteImages = false,
}) => {
  const navigate = useNavigate();
  const eventDetails = useEvent().response;
  const { user } = useUser();
  const theme = useContext(ThemeContext);
  const [width, setWidth] = useState(window.innerWidth);
  const [isNotifying, setIsNotifying] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [notificationTimeout, setNotificationTimeout] = useState(null);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
    // Check if document height is greater than window height
    setHasScrollbar(document.documentElement.scrollHeight > window.innerHeight);
  }

  function handleScroll() {
    setScrollOffset(window.scrollY);
  }

  const checkNotificationStatus = () => {
    try {
      const notificationHistory = JSON.parse(localStorage.getItem(NOTIFICATION_STORAGE_KEY) || '{}');
      const favoriteListKey = `${user.id}_${folderDetails.id}`;
      const lastNotification = notificationHistory[favoriteListKey];
  
      if (lastNotification) {
        const timeSinceLastNotification = Date.now() - lastNotification;

        if (timeSinceLastNotification < NOTIFICATION_COOLDOWN) {
          const remainingTime = NOTIFICATION_COOLDOWN - timeSinceLastNotification;
          
          setIsButtonEnabled(false);          
          const timeoutId = setTimeout(() => {
            checkNotificationStatus();
          }, remainingTime);
          
          setNotificationTimeout(timeoutId);
          return false;
        }
      }
      
      setIsButtonEnabled(true);
      return true;
    } catch (error) {
      console.error('Error checking notification status:', error);
      return true;
    }
  };

  const updateNotificationHistory = () => {
    try {
      const notificationHistory = JSON.parse(localStorage.getItem(NOTIFICATION_STORAGE_KEY) || '{}');
      const favoriteListKey = `${user.id}_${folderDetails.id}`;
      notificationHistory[favoriteListKey] = Date.now();
      localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(notificationHistory));
      
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
        setNotificationTimeout(null);
      }
    } catch (error) {
      console.error('Error updating notification history:', error);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    window.addEventListener("scroll", handleScroll);
    // Initial check
    handleWindowSizeChange();
    handleScroll();
    if (folderDetails.id) {
      checkNotificationStatus();
    }

    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
      window.removeEventListener("scroll", handleScroll);
      // Clean up timeout if component unmounts
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }
    };
  }, [folderDetails.id, user.id]);

  const handleNotifyPhotographer = async () => {
    if (!isButtonEnabled || !folderDetails.id) return;
    
    try {
      setIsNotifying(true);
      const response = await notifyPhotographer(eventDetails.photographer.email, user.email, folderDetails.name, eventDetails.data.name);
      if (!response.error) {
        updateNotificationHistory();
        setIsButtonEnabled(false);
        checkNotificationStatus();
      }else{
        window.Toast.fire({
          icon: "error",
          title: "Something went wrong",
        });
      }
      setIsNotifying(false);
    } catch (error) {
      console.error('Error notifying photographer:', error);
      setIsNotifying(false);
    }
  };

  const isMobile = width <= 768;
  return (
    <div
      className={`items-center dark:text-light-gray text-sm flex justify-start ${containerClasses}`}
    >
      <span
        onClick={(e) => {
          changeGallery(false);
          onFolderClickHandler(e);
          if (addAnchorTag && user.accessLevel) {
            if (ENVIRONMENT === STAGING_ENVIRONMENT_KEY) {
              navigate(
                `/${user.accessLevel ?? "facerec"}/${eventDetails.data.slug}${
                  user.accessLevel === "view"
                    ? ""
                    : `/results/${user.search_id}`
                }`
              );
              return;
            }
            window.location.assign(
              `//${eventDetails.photographer.domain}/${
                user.accessLevel ?? "facerec"
              }/${eventDetails.data.slug}${
                user.accessLevel === "view" ? "" : `/results/${user.search_id}`
              }`
            );
          } else navigate(backLink);
        }}
      >
        <DownArrow
          className={"cursor-pointer rotate-90"}
          color={theme === "dark" ? "white" : "black"}
          height={isMobile ? "12" : "16"}
          width={isMobile ? "12" : "16"}
        />
      </span>
      <div
        className="cursor-pointer text-sm md:text-lg sm:whitespace-nowrap pl-2"
        onClick={(e) => {
          changeGallery(false);
          onFolderClickHandler(e);
          if (addAnchorTag && user.accessLevel) {
            if (ENVIRONMENT === STAGING_ENVIRONMENT_KEY) {
              navigate(
                `/${user.accessLevel ?? "facerec"}/${eventDetails.data.slug}${
                  user.accessLevel === "view"
                    ? ""
                    : `/results/${user.search_id}`
                }`
              );
              return;
            }
            window.location.assign(
              `//${eventDetails.photographer.domain}/${
                user.accessLevel ?? "facerec"
              }/${eventDetails.data.slug}${
                user.accessLevel === "view" ? "" : `/results/${user.search_id}`
              }`
            );
          } else navigate(backLink);
        }}
      >
        {title}
      </div>
      {!!Object.keys(folderDetails).length ? (
        <span
          className={`cursor-pointer text-xs ml-2.5 rounded-[5px] pt-[2px] pb-[2px] pl-[5px] pr-[2px] ${
            theme === "dark" ? "bg-[#555555]" : "bg-[#cccccc]"
          }`}
        >
          {" "}
          {folderDetails.count}{" "}
          {folderDetails.maxFavouritesAllowed
            ? "/" + folderDetails.maxFavouritesAllowed
            : "photos"}{" "}
        </span>
      ) : (
        <></>
      )}
      {showFavouriteImages && folderDetails.id && hasScrollbar && scrollOffset > 0 && (
        <Button
          onClick={handleNotifyPhotographer}
          disabled={!isButtonEnabled || isNotifying}
          className={`fixed right-4 bottom-4 z-50 text-sm ${
            theme === "dark"
              ? isButtonEnabled 
                ? "bg-[rgb(217,70,239)] hover:bg-[rgb(197,50,219)]"
                : "bg-[#333333]"
              : isButtonEnabled
                ? "bg-[rgb(217,70,239)] hover:bg-[rgb(197,50,219)]"
                : "bg-[#e0e0e0]"
          } ${(!isButtonEnabled || isNotifying) ? "opacity-70" : ""}`}
          text={isNotifying ? "Notifying..." : isButtonEnabled ? "Notify photographer" : "Notified"}
        />
      )}
    </div>
  );
};

export default GoBackSection;
